// src/components/ReportsSidebar.tsx
import React from 'react';
import {
  Paper,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { groupByDate } from '../utils/groupByDate';

interface ReportItem {
  id: string;
  title: string;
  created: string; // Include 'created' date
}

interface ReportsSidebarProps {
  reports: ReportItem[];
  isFetchingReports: boolean;
  selectedReportId: string | null;
  handleSelectReport: (reportId: string) => void;
  isGeneratingReport: boolean;
  isFetchingReportContent: boolean;
}

const ReportsSidebar: React.FC<ReportsSidebarProps> = ({
  reports,
  isFetchingReports,
  selectedReportId,
  handleSelectReport,
  isGeneratingReport,
  isFetchingReportContent,
}) => {
  // Group reports by date
  const groupedReports = groupByDate(reports);

  // Sort dates in descending order (most recent first)
  const sortedDates = Object.keys(groupedReports).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <Paper sx={{ height: '85vh', display: 'flex', flexDirection: 'column' }}>
      <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {isFetchingReports ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          sortedDates.map((date) => (
            <React.Fragment key={date}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {date}
              </Typography>
              <Divider />
              {groupedReports[date].map((report: ReportItem) => (
                <ListItemButton
                  key={report.id}
                  selected={report.id === selectedReportId}
                  onClick={() => handleSelectReport(report.id)}
                  disabled={isGeneratingReport || isFetchingReportContent}
                >
                  <ListItemText primary={report.title || 'Untitled Report'} />
                </ListItemButton>
              ))}
            </React.Fragment>
          ))
        )}
      </List>
    </Paper>
  );
};

export default ReportsSidebar;
